import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
    setOpenSubmenu(null);
  }, [location]);
  const navLinks = [{
    name: 'Programs',
    path: '/programs'
  }, {
    name: 'Facility',
    path: '/facility'
  }, {
    name: 'Benefits',
    path: '/child-benefits',
    submenu: [{
      name: 'Child Benefits',
      path: '/child-benefits'
    }, {
      name: 'Teen Benefits',
      path: '/teen-benefits'
    }, {
      name: 'Adult Benefits',
      path: '/adult-benefits'
    }, {
      name: 'Family Benefits',
      path: '/family-benefits'
    }]
  }, {
    name: 'Personal Safety',
    path: '/personal-safety'
  }, {
    name: 'About',
    path: '/about'
  }, {
    name: 'Contact',
    path: '/contact'
  }];
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-sm shadow-lg py-4' : 'bg-transparent py-6'}`} data-id="element-189">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-190">
        <div className="flex justify-between items-center" data-id="element-191">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-id="element-192">
            <div className="relative w-12 h-12 overflow-hidden rounded-full border-2 border-[#00D4FF] bg-black" data-id="element-193">
              <img src="https://cdn.magicpatterns.com/uploads/fmBXjQm2BPtC5bTSRKvwCz/walkerlogo-gigapixel-cgi-3600w.png" alt="Walker's Martial Arts Logo" className="w-full h-full object-cover" data-id="element-194" />
            </div>
            <div className="flex flex-col" data-id="element-195">
              <span className="text-white font-bold text-lg leading-none tracking-wide uppercase" data-id="element-196">
                Walker's
              </span>
              <span className="text-[#00D4FF] font-bold text-sm leading-none tracking-wider uppercase" data-id="element-197">
                Martial Arts
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8" data-id="element-198">
            {navLinks.map(link => <div key={link.name} className="relative group" data-id="element-199">
                {link.submenu ? <>
                    <button className={`transition-colors font-medium text-sm uppercase tracking-wider flex items-center gap-1 ${link.submenu.some(sub => sub.path === location.pathname) ? 'text-[#00D4FF]' : 'text-gray-300 hover:text-[#00D4FF]'}`} data-id="element-200">
                      {link.name}
                      <ChevronDown className="w-4 h-4" data-id="element-201" />
                    </button>
                    {/* Dropdown Menu */}
                    <div className="absolute top-full left-0 mt-2 w-48 bg-black/95 backdrop-blur-sm border border-gray-800 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200" data-id="element-202">
                      {link.submenu.map(sublink => <Link key={sublink.path} to={sublink.path} className={`block px-4 py-3 text-sm font-medium transition-colors ${location.pathname === sublink.path ? 'text-[#00D4FF] bg-[#00D4FF]/10' : 'text-gray-300 hover:text-[#00D4FF] hover:bg-[#00D4FF]/5'}`} data-id="element-203">
                          {sublink.name}
                        </Link>)}
                    </div>
                  </> : <Link to={link.path} className={`transition-colors font-medium text-sm uppercase tracking-wider ${location.pathname === link.path ? 'text-[#00D4FF]' : 'text-gray-300 hover:text-[#00D4FF]'}`} data-id="element-204">
                    {link.name}
                  </Link>}
              </div>)}
            <a href="#schedule" className="bg-[#00D4FF] text-black px-6 py-2.5 rounded font-bold uppercase tracking-wider text-sm hover:bg-[#33ddff] transition-colors shadow-[0_0_15px_rgba(0,212,255,0.3)] hover:shadow-[0_0_25px_rgba(0,212,255,0.5)]" data-id="element-205">
              Free Trial
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsOpen(true)} className="md:hidden text-white p-2 hover:text-[#00D4FF] transition-colors" aria-label="Open menu" data-id="element-206">
            <Menu size={28} data-id="element-207" />
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence data-id="element-208">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsOpen(false)} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 md:hidden" data-id="element-209" />

            {/* Drawer */}
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed top-0 right-0 bottom-0 w-[280px] bg-[#1a1a1a] z-50 shadow-2xl border-l border-gray-800 flex flex-col" data-id="element-210">
              <div className="p-6 flex justify-between items-center border-b border-gray-800" data-id="element-211">
                <span className="text-white font-bold text-lg" data-id="element-212">Menu</span>
                <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white transition-colors" data-id="element-213">
                  <X size={24} data-id="element-214" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto py-6 px-6 flex flex-col gap-4" data-id="element-215">
                <Link to="/" onClick={() => setIsOpen(false)} className="text-lg font-medium text-gray-300 hover:text-[#00D4FF] transition-colors" data-id="element-216">
                  Home
                </Link>
                {navLinks.map(link => <div key={link.name} data-id="element-217">
                    {link.submenu ? <>
                        <button onClick={() => setOpenSubmenu(openSubmenu === link.name ? null : link.name)} className={`w-full text-left text-lg font-medium transition-colors flex items-center justify-between ${link.submenu.some(sub => sub.path === location.pathname) ? 'text-[#00D4FF]' : 'text-gray-300 hover:text-[#00D4FF]'}`} data-id="element-218">
                          {link.name}
                          <ChevronDown className={`w-4 h-4 transition-transform ${openSubmenu === link.name ? 'rotate-180' : ''}`} data-id="element-219" />
                        </button>
                        <AnimatePresence data-id="element-220">
                          {openSubmenu === link.name && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-221">
                              <div className="pl-4 pt-2 space-y-2" data-id="element-222">
                                {link.submenu.map(sublink => <Link key={sublink.path} to={sublink.path} onClick={() => setIsOpen(false)} className={`block py-2 text-sm font-medium transition-colors ${location.pathname === sublink.path ? 'text-[#00D4FF]' : 'text-gray-400 hover:text-[#00D4FF]'}`} data-id="element-223">
                                    {sublink.name}
                                  </Link>)}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </> : <Link to={link.path} onClick={() => setIsOpen(false)} className={`text-lg font-medium transition-colors block ${location.pathname === link.path ? 'text-[#00D4FF]' : 'text-gray-300 hover:text-[#00D4FF]'}`} data-id="element-224">
                        {link.name}
                      </Link>}
                  </div>)}
                <hr className="border-gray-800 my-2" data-id="element-225" />
                <a href="#schedule" onClick={() => setIsOpen(false)} className="bg-[#00D4FF] text-black py-3 rounded text-center font-bold uppercase tracking-wider hover:bg-[#33ddff] transition-colors" data-id="element-226">
                  Schedule Free Trial
                </a>

                <div className="mt-auto pt-8" data-id="element-227">
                  <div className="flex items-center gap-3 text-gray-400 mb-2" data-id="element-228">
                    <Phone size={18} className="text-[#00D4FF]" data-id="element-229" />
                    <span className="text-sm" data-id="element-230">Call us today</span>
                  </div>
                  <p className="text-white font-medium text-lg" data-id="element-231">
                    Bloomington, IL
                  </p>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </nav>;
}